import React from 'react';
import { ChevronLeft, Wind, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import type { LocationDef } from '#entities/location/index.js';
import { LOCATION_TYPE_COLORS } from '#shared/config/colors.js';

interface MapPageProps {
  street: LocationDef;
  buildings: LocationDef[];
  onEnterLocation: (def: LocationDef) => void;
  onBack: () => void;
}

export const MapPage: React.FC<MapPageProps> = ({
  street,
  buildings,
  onEnterLocation,
  onBack,
}) => {
  return (
    <div className="animate-in fade-in zoom-in-95 duration-500">
      {street.imageUrl && (
        <div className="w-[calc(100%+2rem)] -mx-4 sm:mx-0 aspect-[16/7] sm:aspect-video rounded-none sm:rounded-sm border-y sm:border border-border overflow-hidden mb-4 md:mb-8 terminal-card relative">
          <img
            src={street.imageUrl}
            alt={street.name}
            className="w-full h-full object-cover origin-center sepia-[0.3] contrast-[1.1] brightness-[0.8] transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-80" />

          <button
            onClick={onBack}
            className="absolute top-3 left-7 md:top-4 md:left-4 group flex items-center gap-2 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-primary bg-background/80 px-3 py-1.5 backdrop-blur-sm border border-border/50 hover:bg-primary hover:text-primary-foreground transition-all shadow-xl z-20"
          >
            <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Назад
          </button>
        </div>
      )}

      {!street.imageUrl && (
        <button onClick={onBack} className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground mb-8 transition-all">
          <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Назад в город
        </button>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-4 md:mb-8 gap-1">
        <div>
          <h2 className="text-[8px] md:text-[10px] font-black uppercase tracking-[0.4em] text-muted-foreground mb-0.5 md:mb-2">Текущая локация</h2>
          <h1 className="text-xl md:text-3xl font-black uppercase tracking-tighter text-foreground">{street.name}</h1>
        </div>
      </div>

      <p className="text-muted-foreground text-[11px] md:text-sm leading-relaxed mb-4 md:mb-8 max-w-2xl border-l-2 border-primary/30 pl-3 md:pl-4 italic">
        {street.description}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        {buildings.map(def => (
          <div
            key={def.id}
            onClick={() => onEnterLocation(def)}
            className={clsx(
              "group relative p-3 md:p-6 rounded-sm border transition-all duration-500 cursor-pointer overflow-hidden bg-card/60 terminal-card hover:border-primary/50",
              LOCATION_TYPE_COLORS[def.type] ?? "border-border"
            )}
          >
            {def.imageUrl && (
              <>
                <div
                  className="absolute inset-0 z-0 opacity-20 blur-[2px] scale-105 transition-transform duration-700 group-hover:scale-110 bg-cover bg-center sepia-[0.3]"
                  style={{ backgroundImage: `url(${def.imageUrl})` }}
                />
                <div className="absolute inset-0 z-0 bg-gradient-to-t from-card via-card/80 to-transparent" />
              </>
            )}
            <div className="absolute top-0 right-0 w-16 h-16 md:w-32 md:h-32 bg-current opacity-[0.03] -mr-4 -mt-4 md:-mr-8 md:-mt-8 rounded-full group-hover:scale-150 transition-transform duration-700 z-10" />

            <div className="relative z-10">
              <div className="flex justify-between items-start mb-3 md:mb-6">
                <div className="w-8 h-8 md:w-12 md:h-12 rounded-sm bg-input border border-border flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl">
                  <Wind size={16} className="md:size-24 text-foreground/80" />
                </div>
                <div className="text-[9px] md:text-[10px] font-black px-1.5 py-0.5 rounded-sm bg-input border border-border tracking-[0.1em] md:tracking-[0.2em] uppercase text-muted-foreground">
                  L{def.dangerLevel}
                </div>
              </div>
              <h3 className="text-sm md:text-xl font-black uppercase tracking-tight mb-1 md:mb-2 text-foreground group-hover:translate-x-1 transition-transform truncate">
                {def.name}
              </h3>
              <p className="text-[11px] md:text-xs text-muted-foreground leading-tight md:leading-relaxed mb-2 md:mb-6 h-6 md:h-8 line-clamp-2">
                {def.description}
              </p>
              <div className="flex items-center justify-end pt-2 md:pt-4 border-t border-border/20">
                <ChevronRight size={10} className="md:size-12 text-primary opacity-50 group-hover:opacity-100" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
